import React from "react";
import { _goToCart } from "router/helper";

import { View, StyleSheet, TouchableOpacity, Platform } from "react-native";
import {
  Icon,
  Text,
  Button,
  Shadow,
  ConfirmAlert,
  ConfirmAlertCard,
  FullScreenLoading,
} from "component";

import { css } from "style";
import { useFlag } from "hook";
import { cartAtom } from "store";
import { priceFormatter, sn } from "utils";
import { sizes, colors, strings } from "asset/constant";

import { useRemoveCart } from "client/api";
import { useRecoilValue } from "recoil";

const Trapezoid = (props: { width?: number; color?: string }) => {
  const { width = 225, color = colors.theme } = props;

  const trapezoidStyles = {
    width,
    height: 0,
    borderBottomWidth: 60,
    borderBottomColor: color,
    borderLeftWidth: 50,
    borderLeftColor: "transparent",
    borderRightWidth: 50,
    borderRightColor: "transparent",
  };

  return (
    <View
      style={{
        ...trapezoidStyles,
        ...css.fix,
        ...Platform.select({
          web: {
            right: -90,
          },
          native: {
            left: -90,
          },
        }),
      }}
      {...props}
    />
  );
};

interface FooterScreenCartSummaryButtonT {
  icon?: IconT;
  title?: string;
  onPress?: () => void;
}

export default function FooterScreenCartSummary(props: {
  button?: FooterScreenCartSummaryButtonT;
}) {
  const cart = useRecoilValue(cartAtom);

  const hasCustomized = cart?.products
    ?.map((p) => p.attribute_items)
    .flat()
    .some((e) => e.is_customized === 1);

  const { button } = props;

  const { flg: alert, on: showAlert, off: hideAlert } = useFlag();

  const removeCartMut = useRemoveCart();

  const _onRemoveCart = () => removeCartMut.mutate();

  return (
    <>
      <View style={styles.container}>
        <View style={styles.contentContainer}>
          <>
            <Trapezoid width={232} color={colors.black + "05"} />
            <Trapezoid width={227} color={colors.black + "11"} />
            <Trapezoid width={224} color={colors.black + "11"} />
            <Trapezoid />
          </>

          <View style={styles.viewCartContainer}>
            <TouchableOpacity
              activeOpacity={sizes.tapOpacity}
              onPress={button?.onPress || _goToCart}
              style={{ ...css.alignFEnd, paddingRight: 5 }}
            >
              <View style={styles.viewCart}>
                <Icon
                  name={"shoppingcart"}
                  color={colors.theme}
                  size={20}
                  {...button?.icon}
                />
              </View>
              <Text weight="extraBold" color={colors.white}>
                {button?.title || strings.view_cart}
              </Text>
            </TouchableOpacity>
          </View>

          <View style={{ ...css.container, ...css.justifyCenter }}>
            <View
              style={{
                ...css.rRowCenterAlign,
                marginLeft: 5,
                top: 4,
              }}
            >
              <View
                style={{
                  height: 22,
                  minWidth: 22,
                  paddingHorizontal: 5,
                  ...css.centralElements,
                  backgroundColor: colors.theme,
                  borderRadius: 9,
                }}
              >
                <Text weight="bold" color={colors.white}>
                  {cart?.products?.reduce(
                    (sum, item) => sum + Number(item.count),
                    0
                  )}
                </Text>
              </View>
              <Text
                weight="bold"
                style={{ bottom: 1 }}
                color={colors.text.dark}
              >
                {" "}
                {strings.products_in_your_cart}
              </Text>
            </View>
            <View
              style={{
                ...css.rRowCenterAlign,
                top: sn(4, 4, 5, 6),
                marginLeft: 20,
              }}
            >
              {Platform.OS === "web" && <View style={{ width: 15 }} />}

              {hasCustomized && (
                <>
                  <Icon
                    type="fontAwesome"
                    name="level-up"
                    color={colors.theme}
                    size={20}
                    style={{ left: 2 }}
                  />
                  <Icon
                    type="materialCommunity"
                    name="plus-thick"
                    color={colors.theme}
                    style={{ right: 2, top: 2 }}
                    size={12}
                  />
                </>
              )}

              <View style={{ marginHorizontal: 4 }}>
                <Text weight="black" size={"xxxl"} color={colors.text.dark}>
                  {priceFormatter(cart?.total)}
                </Text>
              </View>
              <Text weight="extraBold" size={"sm"}>
                {strings.currency}
              </Text>
            </View>
          </View>
          <View style={styles.deleteContainer}>
            <Button
              onPress={showAlert}
              bordered={{ width: 1, color: colors.border.lightest }}
              bgColor={colors.white}
              style={{ borderRadius: 17, top: 2 }}
              square={42}
              icon={{ name: "delete", color: colors.danger, size: 25 }}
            />
          </View>
        </View>
      </View>

      <ConfirmAlert
        force
        show={alert}
        hide={hideAlert}
        renderContent={(close) => (
          <ConfirmAlertCard
            icon={{ name: "delete" }}
            title={strings.remove_cart}
            message={strings.you_want_remove_cart}
            subMessage={strings.are_you_sure}
            noTitle={strings.cancel}
            onNoPress={close}
            yesTitle={strings.remove_cart}
            onYesPress={() => {
              close();
              _onRemoveCart();
            }}
          />
        )}
      />

      {removeCartMut.isLoading && <FullScreenLoading />}
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    height: 60,
    bottom: 0,
    position: "absolute",
  },

  contentContainer: {
    width: "100%",
    borderTopLeftRadius: 20,
    flexDirection: "row-reverse",
    backgroundColor: colors.white,
    height: "100%",
    borderTopWidth: 2,
    borderLeftWidth: 1,
    borderColor: colors.border.lightest,
  },

  trapezoid: {
    width: 230,
    height: 0,
    position: "absolute",
    left: -90,
    borderBottomWidth: 80,
    borderBottomColor: colors.theme,
    borderLeftWidth: 50,
    borderLeftColor: "transparent",
    borderRightWidth: 50,
    borderRightColor: "transparent",
  },

  viewCartContainer: {
    width: 118,
    ...css.justifyCenter,
    ...css.alignFEnd,
    paddingRight: 5,
  },

  viewCart: {
    top: 2,
    width: 28,
    height: 28,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.white,
  },

  deleteContainer: { height: "100%", aspectRatio: 1, ...css.centralElements },
});
